import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type PortalMessage } from "@clevis/shared-types";
import { Button, Card, CardTitle, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";

// Customer page → Portal messages: what the people at this customer wrote
// from their portal, and the office's replies (they're emailed a heads-up).

export function PortalMessages({ tenantId, customerId }: { tenantId: string; customerId: string }) {
  const [rows, setRows] = useState<Array<PortalMessage & { id: string }>>([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantPortalMessages(tenantId)), where("customerId", "==", customerId), orderBy("at", "asc"), limit(200)),
        (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PortalMessage) }))),
        () => undefined,
      ),
    [tenantId, customerId],
  );
  const unread = rows.filter((m) => m.direction === "in" && !m.readAt).length;
  if (rows.length === 0) return null;

  async function send() {
    if (!body.trim()) return;
    setBusy(true);
    setNote("");
    try {
      const r = await call<{ emailed: number }>("replyPortalMessage", { tenantId, customerId, body: body.trim() });
      setBody("");
      setNote(r.emailed ? `Sent; ${r.emailed} ${r.emailed === 1 ? "person was" : "people were"} emailed.` : "Sent; they'll see it in the portal.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-2">
      <CardTitle className="mb-0">
        Portal messages{unread ? <span className="ml-2 rounded-full bg-brandorange px-2 py-0.5 text-[11px] text-white">{unread} new</span> : null}
      </CardTitle>
      <div className="max-h-72 space-y-1 overflow-y-auto text-sm">
        {rows.map((m) => (
          <div key={m.id} className={cn("max-w-[85%] rounded-lg px-2.5 py-1.5", m.direction === "in" ? "bg-cream text-ink" : "ml-auto bg-ink/90 text-white")}>
            <div className="whitespace-pre-wrap">{m.body}</div>
            <div className={cn("mt-0.5 text-[11px]", m.direction === "in" ? "text-steel" : "text-white/70")}>
              {m.direction === "in" ? m.contactName || "Customer" : m.byName || "Office"} ·{" "}
              {new Date(m.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              {m.direction === "in" && !m.readAt ? " · new" : ""}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <textarea
          className="min-h-[44px] flex-1 rounded-lg border border-steel/30 bg-white px-2 py-1 text-sm text-ink focus:border-brandorange focus:outline-none"
          placeholder="Reply…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button busy={busy} className="px-3 py-1.5 text-sm" onClick={() => void send()}>
          Send
        </Button>
      </div>
      {note ? <p className="text-[11px] text-steel">{note}</p> : null}
    </Card>
  );
}
